// pages/sop/SopFoldersList.jsx
import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Skeleton,
  Menu,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

// ─── Design tokens (matched to FoldersList) ─────────────────────────────────
const TEAL = "#0c8a9b";
const TEAL_DARK = "#0a7a8a";
const TEAL_LIGHT = "#e6f6f8";
const FONT = "'DM Sans', 'Inter', sans-serif";

const FOLDER_ICONS = [
  { bg: "#e8f5f7", color: TEAL },
  { bg: "#fef3e2", color: "#e07b00" },
  { bg: "#edf2ff", color: "#3b5bdb" },
  { bg: "#f3f0ff", color: "#7048e8" },
  { bg: "#e6fcf5", color: "#0ca678" },
  { bg: "#fff4e6", color: "#d9480f" },
];

// ─── Keyframes ──────────────────────────────────────────────────────────────
const KF_ID = "sop-folders-kf";
if (typeof document !== "undefined" && !document.getElementById(KF_ID)) {
  const s = document.createElement("style");
  s.id = KF_ID;
  s.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');
    @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  `;
  document.head.appendChild(s);
}

// ─── TextField sx ────────────────────────────────────────────────────────────
const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    fontFamily: FONT,
    fontSize: "0.9rem",
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: TEAL },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: TEAL },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: TEAL },
};

// ─── Skeleton card ───────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <Card
    sx={{
      borderRadius: "16px",
      boxShadow: "0 1px 4px rgba(0,0,0,.06)",
      background: "#fff",
    }}
  >
    <CardContent sx={{ p: 2.5 }}>
      <Skeleton
        variant="rounded"
        width={40}
        height={40}
        sx={{ borderRadius: "10px", mb: 1.5 }}
      />
      <Skeleton variant="text" width="55%" height={22} />
      <Skeleton variant="text" width="75%" height={16} sx={{ mt: 0.5 }} />
    </CardContent>
  </Card>
);

// ─── Folder Menu ─────────────────────────────────────────────────────────────
const FolderMenu = ({ folder, onEdit, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        sx={{
          color: "#adb5bd",
          "&:hover": { color: "#495057" },
          borderRadius: "6px",
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0,0,0,.12)",
            minWidth: 140,
            fontFamily: FONT,
          },
        }}
      >
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(null);
            onEdit(folder);
          }}
          sx={{ fontFamily: FONT, fontSize: "0.85rem", gap: 1 }}
        >
          <EditIcon sx={{ fontSize: 15, color: "#868e96" }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(null);
            onDelete(folder.id);
          }}
          sx={{
            fontFamily: FONT,
            fontSize: "0.85rem",
            color: "#e03131",
            gap: 1,
          }}
        >
          <DeleteIcon sx={{ fontSize: 15 }} /> Delete
        </MenuItem>
      </Menu>
    </>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const SopFoldersList = () => {
  const navigate = useNavigate();
  const { userInfo } = useContext(AutoTaskAuthContext);
  const store_id = userInfo?.store_id;

  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderName, setFolderName] = useState("");
  const [folderDesc, setFolderDesc] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);

  const fetchFolders = async () => {
    if (store_id === null || store_id === undefined) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/sop-manager/sop-folders`, {
        params: { store_id },
      });
      setFolders(res.data.data || []);
      setError("");
    } catch (err) {
      console.error(err);
      setError("Failed to load folders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFolders();
  }, [store_id]);

  const handleOpenModal = (folder = null) => {
    if (folder) {
      setEditingFolder(folder);
      setFolderName(folder.name);
      setFolderDesc(folder.description || "");
    } else {
      setEditingFolder(null);
      setFolderName("");
      setFolderDesc("");
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingFolder(null);
    setFolderName("");
    setFolderDesc("");
  };

  const handleSave = async () => {
    if (!folderName.trim()) {
      alert("Folder name is required");
      return;
    }
    setSubmitLoading(true);
    try {
      if (editingFolder) {
        await axios.put(
          `${API_BASE}/api/sop-manager/sop-folders/${editingFolder.id}`,
          {
            name: folderName,
            description: folderDesc,
          },
        );
      } else {
        await axios.post(`${API_BASE}/api/sop-manager/sop-folders`, {
          store_id,
          name: folderName,
          description: folderDesc,
        });
      }
      fetchFolders();
      handleCloseModal();
    } catch (err) {
      alert(err.response?.data?.statusMessage || "Failed to save folder");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Delete this folder? All SOPs inside will also be deleted.",
      )
    )
      return;
    try {
      await axios.delete(`${API_BASE}/api/sop-manager/sop-folders/${id}`);
      fetchFolders();
    } catch (err) {
      alert("Failed to delete folder");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "80vh",
        background: "#f8f9fa",
        borderRadius: 0.4,
        fontFamily: FONT,
        p: { xs: 2, sm: 3.5 },
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3.5,
          animation: "fadeUp .4s ease both",
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontFamily: FONT,
              color: "#1a1d23",
              fontSize: "1.65rem",
              letterSpacing: "-0.3px",
            }}
          >
            SOP Folders
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#868e96",
              mt: 0.4,
              fontFamily: FONT,
              fontSize: "0.875rem",
            }}
          >
            Organize your standard operating procedures by department.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: "16px !important" }} />}
          onClick={() => handleOpenModal()}
          sx={{
            background: TEAL,
            color: "#fff",
            borderRadius: "10px",
            px: 2,
            py: 0.9,
            fontFamily: FONT,
            fontWeight: 600,
            fontSize: "0.85rem",
            textTransform: "none",
            boxShadow: "none",
            whiteSpace: "nowrap",
            "&:hover": { background: TEAL_DARK, boxShadow: "none" },
          }}
        >
          New Folder
        </Button>
      </Box>

      {/* ── Error ─────────────────────────────────────────────────────── */}
      {error && (
        <Alert
          severity="error"
          sx={{ borderRadius: "12px", mb: 3, fontFamily: FONT }}
        >
          {error}
        </Alert>
      )}

      {/* ── Loading ───────────────────────────────────────────────────── */}
      {loading && (
        <Grid container spacing={2.5}>
          {[...Array(4)].map((_, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <SkeletonCard />
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Empty state ───────────────────────────────────────────────── */}
      {!loading && !error && folders.length === 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: 10,
            animation: "fadeUp .5s ease both",
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: TEAL_LIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <FolderOutlinedIcon sx={{ fontSize: 34, color: TEAL }} />
          </Box>
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{ fontFamily: FONT, color: "#1a1d23" }}
          >
            No folders yet
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#868e96", mt: 0.5, fontFamily: FONT }}
          >
            Create your first folder to organise your SOPs.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
            sx={{
              mt: 2.5,
              background: TEAL,
              borderRadius: "10px",
              fontFamily: FONT,
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { background: TEAL_DARK, boxShadow: "none" },
            }}
          >
            Create Folder
          </Button>
        </Box>
      )}

      {/* ── Folder Cards ──────────────────────────────────────────────── */}
      {!loading && folders.length > 0 && (
        <>
          {/* Section Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2.2,
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT,
                fontWeight: 700,
                fontSize: "1.1rem",
                color: "#111827",
                letterSpacing: "-0.2px",
              }}
            >
              All Folders
            </Typography>
          </Box>

          <Grid container spacing={2.2}>
            {folders.map((folder, idx) => {
              const ic = FOLDER_ICONS[idx % FOLDER_ICONS.length];
              const delay = `${idx * 0.05}s`;
              // Backend field name for SOP count may vary — check the common variants
              const sopCount =
                folder.sop_count ??
                folder.sops_count ??
                folder.total_sops ??
                folder.sopCount ??
                (Array.isArray(folder.sops) ? folder.sops.length : 0) ??
                0;

              return (
                <Grid item xs={12} sm={6} md={3} key={folder.id}>
                  <Card
                    onClick={() => navigate(`/ats/sop/folder/${folder.id}`)}
                    sx={{
                      borderRadius: "18px",
                      background: "#fff",
                      border: "1px solid #edf0f2",
                      boxShadow: "0 1px 2px rgba(16,24,40,.04)",
                      cursor: "pointer",
                      minHeight: 170,
                      overflow: "hidden",
                      position: "relative",
                      animation: `fadeUp .45s ease ${delay} both`,
                      transition: "all .22s ease",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: "0 10px 28px rgba(16,24,40,.08)",
                        borderColor: "rgba(12,138,155,.28)",
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 2.3,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* top */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          mb: 2.3,
                        }}
                      >
                        <Box
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: "12px",
                            background: ic.bg,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <FolderOutlinedIcon
                            sx={{
                              color: ic.color,
                              fontSize: 22,
                            }}
                          />
                        </Box>

                        <Box onClick={(e) => e.stopPropagation()}>
                          <FolderMenu
                            folder={folder}
                            onEdit={handleOpenModal}
                            onDelete={handleDelete}
                          />
                        </Box>
                      </Box>

                      {/* title */}
                      <Typography
                        sx={{
                          fontFamily: FONT,
                          fontWeight: 600,
                          fontSize: "0.95rem",
                          color: "#111827",
                          lineHeight: 1.2,
                          mb: 0.4,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {folder.name}
                      </Typography>

                      {/* description */}
                      <Typography
                        sx={{
                          fontFamily: FONT,
                          fontSize: "0.77rem",
                          color: "#6b7280",
                          fontWeight: 500,
                          mb: 1.6,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          wordBreak: "break-word",
                          lineHeight: 1.35,
                        }}
                      >
                        {folder.description
                          ? folder.description
                          : `${sopCount} SOP${sopCount === 1 ? "" : "s"}`}
                      </Typography>

                      <Box sx={{ flexGrow: 1 }} />

                      {/* bottom meta */}
                      <Typography
                        sx={{
                          fontFamily: FONT,
                          fontSize: "0.72rem",
                          color: "#adb5bd",
                          fontWeight: 600,
                        }}
                      >
                        {sopCount} SOP{sopCount === 1 ? "" : "s"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {/* ── Create / Edit Modal ──────────────────────────────────────── */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 60px rgba(0,0,0,.15)",
            fontFamily: FONT,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: FONT,
            fontWeight: 700,
            fontSize: "1rem",
            color: "#1a1d23",
            pt: 2.5,
            pb: 0.5,
          }}
        >
          {editingFolder ? "Edit Folder" : "Create New Folder"}
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            required
            sx={inputSx}
          />
          <TextField
            margin="dense"
            label="Description (optional)"
            fullWidth
            multiline
            rows={2}
            value={folderDesc}
            onChange={(e) => setFolderDesc(e.target.value)}
            sx={inputSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
          <Button
            onClick={handleCloseModal}
            sx={{
              fontFamily: FONT,
              fontWeight: 500,
              color: "#868e96",
              borderRadius: "8px",
              textTransform: "none",
              "&:hover": { background: "#f1f3f5" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={submitLoading}
            sx={{
              background: TEAL,
              borderRadius: "8px",
              fontFamily: FONT,
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { background: TEAL_DARK, boxShadow: "none" },
            }}
          >
            {submitLoading ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : editingFolder ? (
              "Save Changes"
            ) : (
              "Create Folder"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SopFoldersList;
